import { selectedTabSearchParam } from '~/components/global/constants';
import { PropertyTab } from '~/components/portfolio/propertyTabs';
import { InvoiceTypeKey } from '~/data/schemas/invoice';
import { EntryTypeKey } from '~/data/schemas/ticket';

type MergeSearchParamsOptions = {
  currentSearchParams: URLSearchParams;
  newSearchParams: URLSearchParams;
  paramsToKeep?: string[];
};

/**
 * Merges two URLSearchParams objects, keeping only the params in paramsToKeep
 * @param currentSearchParams - The current URLSearchParams object
 * @param newSearchParams - The new URLSearchParams object to merge
 * @returns A new URLSearchParams object with the merged params
 */
export const mergeSearchParams = ({
  currentSearchParams,
  newSearchParams,
}: MergeSearchParamsOptions) => {
  const mergedSearchParams = new URLSearchParams(currentSearchParams);

  // Remove params that would create duplicates
  for (const [paramName] of currentSearchParams) {
    if (newSearchParams.has(paramName)) {
      mergedSearchParams.delete(paramName);
    }
  }

  for (const [paramName, paramValue] of newSearchParams) {
    if (paramValue) {
      mergedSearchParams.append(paramName, paramValue);
    }
  }

  return mergedSearchParams;
};

type TicketUrlParams = {
  carId?: string;
  entryType?: EntryTypeKey[];
  number?: string;
  ticketId?: string;
  propertyName?: string;
};

/**
 * Builds URLSearchParams for ticket URLs with the given parameters
 */
const buildTicketSearchParams = ({
  carId,
  entryType,
  number,
  ticketId,
  propertyName,
}: TicketUrlParams): URLSearchParams => {
  const params = new URLSearchParams();

  if (carId) {
    params.append('car_id', carId);
  }

  if (number) {
    params.append('number', number);
  }

  if (entryType) {
    for (const type of entryType) {
      params.append('entry_type', type);
    }
  }

  if (ticketId) {
    params.append('ticketId', ticketId);
  }

  if (propertyName) {
    params.append('propertyName', propertyName);
  }

  return params;
};

export const buildUrlForTickets = (params: TicketUrlParams): string => {
  const searchParams = buildTicketSearchParams(params);
  return `/tickets?${searchParams.toString()}`;
};

type InvoiceUrlParams = {
  type?: InvoiceTypeKey[];
  kind?: string;
  status?: string[];
  invoiceId?: string;
};

/**
 * Builds URLSearchParams for invoice URLs with the given parameters
 */
const buildSpendingSearchParams = ({
  type,
  kind,
  status,
  invoiceId,
}: InvoiceUrlParams): URLSearchParams => {
  const params = new URLSearchParams();

  if (type) {
    for (const t of type) {
      params.append('invoice_type', t);
    }
  }

  if (kind) {
    params.append('invoice_kind', kind);
  }

  if (status) {
    for (const s of status) {
      params.append('invoice_status', s);
    }
  }

  if (invoiceId) {
    params.append('selectedInvoiceId', invoiceId);
  }

  return params;
};

export const buildUrlForInvoices = (params: InvoiceUrlParams): string => {
  const searchParams = buildSpendingSearchParams(params);
  return `/spending?${searchParams.toString()}`;
};

/** The one place that knows where a property page lives. `/portfolio/:id` redirects here. */
export const buildUrlForProperty = (propertyId: string, searchParams?: URLSearchParams): string => {
  const search = searchParams?.toString();
  const path = `/properties/${propertyId}`;

  return search ? `${path}?${search}` : path;
};

type PropertyTicketUrlParams = TicketUrlParams & {
  propertyId: string;
};

export const buildUrlForPropertyTickets = (params: PropertyTicketUrlParams): string => {
  const searchParams = buildTicketSearchParams(params);
  searchParams.set(selectedTabSearchParam, PropertyTab.Tickets);

  return buildUrlForProperty(params.propertyId, searchParams);
};

type PropertyInvoiceUrlParams = InvoiceUrlParams & {
  propertyId: string;
};

export const buildUrlForPropertySpending = (params: PropertyInvoiceUrlParams): string => {
  const searchParams = buildSpendingSearchParams(params);
  searchParams.set(selectedTabSearchParam, PropertyTab.Spending);

  return buildUrlForProperty(params.propertyId, searchParams);
};

/**
 * The global filter params — the subset of the always-kept params that means the
 * same thing on every page. `selectedInvoiceId` is in that set but not here: it is a
 * redirect to one invoice's page, and carrying it onto another page would bounce the
 * reader off that page too.
 */
const globalFilterParams = ['start_date', 'end_date', 'portfolio', 'service_provider', 'region'];

/**
 * Carries the reader's period and global filters across a jump between two detail
 * pages — without them the destination's loader falls back to its default range.
 */
const withGlobalFilters = (pathname: string, from?: URLSearchParams): string => {
  const search = new URLSearchParams();
  for (const key of globalFilterParams) {
    for (const value of from?.getAll(key) ?? []) {
      search.append(key, value);
    }
  }

  const query = search.toString();
  return query ? `${pathname}?${query}` : pathname;
};

/** Where an elevator's page lives. */
export const buildUrlForElevator = (carId: string, from?: URLSearchParams): string =>
  withGlobalFilters(`/compliance/${carId}`, from);

/**
 * Where a contract's page lives, for a row that opens it in a new tab and so needs the
 * destination itself rather than a param on the current URL.
 */
export const buildUrlForContract = (contractId: string, from?: URLSearchParams): string =>
  withGlobalFilters(`/contracts/${contractId}`, from);

/** Where an invoice's (or proposal's) page lives. */
export const buildUrlForInvoice = (invoiceId: string, from?: URLSearchParams): string =>
  withGlobalFilters(`/spending/${invoiceId}`, from);
